/**
 * Admin page: manage Client Viewer credentials and per-client page access.
 * Only visible to admin users.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Plus, Trash2, KeyRound, Send, Users, ShieldCheck, BellRing, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ViewerManagementPage() {
  const [viewers, setViewers] = useState([]);
  const [pages, setPages] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create viewer dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newViewer, setNewViewer] = useState({ name: "", email: "", password: "" });

  // Access dialog
  const [accessDialog, setAccessDialog] = useState(null); // {viewer, clientId, allowedPages}

  // Password reset dialog
  const [pwDialog, setPwDialog] = useState(null); // {viewer, password}

  // Push notification dialog
  const [pushDialog, setPushDialog] = useState(null); // {viewer, title, body}

  // Auto-alerts dialog
  const [alertsDialog, setAlertsDialog] = useState(null); // {plan, year, running}

  const loadAll = async () => {
    setLoading(true);
    try {
      const [v, p, c] = await Promise.all([
        axios.get(`${API}/admin/viewers`),
        axios.get(`${API}/admin/viewer-pages`),
        axios.get(`${API}/clients`),
      ]);
      setViewers(v.data || []);
      setPages(p.data || []);
      setClients(c.data || []);
    } catch (e) {
      toast.error("Errore caricamento dati: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const createViewer = async () => {
    if (!newViewer.email || !newViewer.password || !newViewer.name) {
      toast.error("Compila tutti i campi");
      return;
    }
    try {
      await axios.post(`${API}/admin/viewers`, newViewer);
      toast.success("Account creato");
      setCreateOpen(false);
      setNewViewer({ name: "", email: "", password: "" });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const deleteViewer = async (viewer) => {
    if (!window.confirm(`Eliminare l'accesso di ${viewer.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/viewers/${viewer.id}`);
      toast.success("Eliminato");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const openAccessDialog = (viewer, existing = null) => {
    setAccessDialog({
      viewer,
      clientId: existing?.client_id || "",
      allowedPages: existing?.allowed_pages || ["overview"],
      isNew: !existing,
    });
  };

  const saveAccess = async () => {
    const { viewer, clientId, allowedPages } = accessDialog;
    if (!clientId) {
      toast.error("Seleziona un cliente");
      return;
    }
    try {
      await axios.post(`${API}/admin/viewers/${viewer.id}/access`, {
        client_id: clientId,
        allowed_pages: allowedPages,
      });
      toast.success("Accesso aggiornato");
      setAccessDialog(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const revokeAccess = async (viewerId, clientId) => {
    if (!window.confirm("Rimuovere l'accesso a questo cliente?")) return;
    try {
      await axios.delete(`${API}/admin/viewers/${viewerId}/access/${clientId}`);
      toast.success("Accesso rimosso");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const resetPassword = async () => {
    const { viewer, password } = pwDialog;
    if (!password || password.length < 6) {
      toast.error("Password troppo corta (min 6)");
      return;
    }
    try {
      await axios.patch(`${API}/admin/viewers/${viewer.id}`, { password });
      toast.success("Password aggiornata");
      setPwDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const sendPush = async () => {
    const { viewer, title, body } = pushDialog;
    if (!title || !body) {
      toast.error("Compila titolo e messaggio");
      return;
    }
    try {
      const r = await axios.post(`${API}/admin/push/send`, {
        viewer_id: viewer.id, title, body,
      });
      toast.success(`Inviata: ${r.data.success} OK / ${r.data.failed} errori`);
      setPushDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  // ---- Auto-alerts ----
  const openAlertsPreview = async () => {
    const year = new Date().getFullYear();
    setAlertsDialog({ plan: null, year, running: false, mode: "preview" });
    try {
      const r = await axios.get(`${API}/admin/alerts/preview?year=${year}`);
      setAlertsDialog({ plan: r.data.plan, year, running: false, mode: "preview" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
      setAlertsDialog(null);
    }
  };

  const runAlerts = async (force = false) => {
    if (!alertsDialog) return;
    setAlertsDialog({ ...alertsDialog, running: true });
    try {
      const r = await axios.post(`${API}/admin/alerts/run`, { year: alertsDialog.year, force });
      toast.success(`Avvisi: ${r.data.pushed} inviati, ${r.data.skipped} saltati, ${r.data.failed} falliti`);
      setAlertsDialog({ ...alertsDialog, running: false, result: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
      setAlertsDialog({ ...alertsDialog, running: false });
    }
  };

  return (
    <div className="container max-w-6xl mx-auto p-4 md:p-8 space-y-6" data-testid="viewer-mgmt-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
            <ShieldCheck className="h-7 w-7 text-blue-600" />
            Accessi Cliente (sola lettura)
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Crea credenziali per i tuoi clienti, scegli quali pagine vedono e invia notifiche push.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={openAlertsPreview} data-testid="run-alerts-btn">
            <BellRing className="h-4 w-4 mr-2" />
            Controllo avvisi
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="create-viewer-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuovo accesso cliente
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-slate-500">Caricamento...</p>
      ) : viewers.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-slate-500">
            <Users className="h-10 w-10 mx-auto mb-3 text-slate-400" />
            Nessun accesso cliente creato. Clicca "Nuovo accesso cliente" per iniziare.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {viewers.map((v) => (
            <Card key={v.id} data-testid={`viewer-card-${v.id}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <span className="text-lg">{v.name}</span>
                    <span className="ml-2 text-sm font-normal text-slate-500">{v.email}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPwDialog({ viewer: v, password: "" })}>
                      <KeyRound className="h-3.5 w-3.5 mr-1" /> Password
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setPushDialog({ viewer: v, title: "", body: "" })}>
                      <Send className="h-3.5 w-3.5 mr-1" /> Notifica
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteViewer(v)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Clienti accessibili</p>
                  <Button size="sm" variant="ghost" onClick={() => openAccessDialog(v)}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi cliente
                  </Button>
                </div>
                {v.access.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">Nessun cliente assegnato</p>
                ) : (
                  <div className="space-y-2">
                    {v.access.map((a) => (
                      <div
                        key={a.client_id}
                        className="flex items-center justify-between bg-slate-50 rounded-lg p-3 flex-wrap gap-2"
                      >
                        <div className="flex-1">
                          <div className="font-medium">{a.client_name}</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {a.allowed_pages.length === 0 ? (
                              <span className="text-xs text-slate-400">Solo cruscotto</span>
                            ) : (
                              a.allowed_pages.map((p) => {
                                const lbl = pages.find((pp) => pp.key === p)?.label || p;
                                return <Badge key={p} variant="secondary" className="text-xs">{lbl}</Badge>;
                              })
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openAccessDialog(v, a)}>
                            Modifica pagine
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => revokeAccess(v.id, a.client_id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo accesso cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome / Etichetta</Label>
              <Input value={newViewer.name} onChange={(e) => setNewViewer({ ...newViewer, name: e.target.value })} placeholder="Es. Spiragli" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={newViewer.email} onChange={(e) => setNewViewer({ ...newViewer, email: e.target.value })} placeholder="cliente@esempio.it" />
            </div>
            <div>
              <Label>Password (almeno 6 caratteri)</Label>
              <Input type="text" value={newViewer.password} onChange={(e) => setNewViewer({ ...newViewer, password: e.target.value })} placeholder="••••••" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
            <Button onClick={createViewer} data-testid="create-viewer-confirm">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access dialog */}
      <Dialog open={!!accessDialog} onOpenChange={(o) => !o && setAccessDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {accessDialog?.isNew ? "Aggiungi cliente a" : "Modifica pagine di"} {accessDialog?.viewer?.name}
            </DialogTitle>
          </DialogHeader>
          {accessDialog && (
            <div className="space-y-4">
              <div>
                <Label>Cliente</Label>
                <Select
                  value={accessDialog.clientId}
                  onValueChange={(v) => setAccessDialog({ ...accessDialog, clientId: v })}
                  disabled={!accessDialog.isNew}
                >
                  <SelectTrigger><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="block mb-2">Pagine visibili</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Il "Cruscotto" è sempre visibile. Seleziona le pagine aggiuntive che il cliente può consultare.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto border rounded-lg p-3">
                  {pages.map((p) => {
                    const checked = accessDialog.allowedPages.includes(p.key);
                    return (
                      <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            const set = new Set(accessDialog.allowedPages);
                            if (v) set.add(p.key); else set.delete(p.key);
                            setAccessDialog({ ...accessDialog, allowedPages: Array.from(set) });
                          }}
                        />
                        {p.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessDialog(null)}>Annulla</Button>
            <Button onClick={saveAccess}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog open={!!pwDialog} onOpenChange={(o) => !o && setPwDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cambia password — {pwDialog?.viewer.name}</DialogTitle></DialogHeader>
          {pwDialog && (
            <div>
              <Label>Nuova password (min 6)</Label>
              <Input
                type="text"
                value={pwDialog.password}
                onChange={(e) => setPwDialog({ ...pwDialog, password: e.target.value })}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwDialog(null)}>Annulla</Button>
            <Button onClick={resetPassword}>Aggiorna</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Push notification dialog */}
      <Dialog open={!!pushDialog} onOpenChange={(o) => !o && setPushDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invia notifica a {pushDialog?.viewer.name}</DialogTitle></DialogHeader>
          {pushDialog && (
            <div className="space-y-3">
              <div>
                <Label>Titolo</Label>
                <Input value={pushDialog.title} onChange={(e) => setPushDialog({ ...pushDialog, title: e.target.value })} />
              </div>
              <div>
                <Label>Messaggio</Label>
                <Input value={pushDialog.body} onChange={(e) => setPushDialog({ ...pushDialog, body: e.target.value })} />
              </div>
              <p className="text-xs text-slate-500">
                Il cliente riceverà la notifica solo se ha attivato le notifiche dal suo dispositivo.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushDialog(null)}>Annulla</Button>
            <Button onClick={sendPush}>Invia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Auto alerts preview/run dialog */}
      <Dialog open={!!alertsDialog} onOpenChange={(o) => !o && setAlertsDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Controllo avvisi automatici — {alertsDialog?.year}</DialogTitle>
          </DialogHeader>
          {alertsDialog && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Vengono inviate notifiche push ai clienti i cui indicatori finanziari mostrano criticità
                (EBITDA &lt; 5%, fatturato in calo &gt;10% YoY, mese o anno in perdita).
                Ogni avviso viene inviato una sola volta per cliente / mese.
              </p>
              {alertsDialog.plan === null ? (
                <p className="text-slate-500 text-sm flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Caricamento anteprima...
                </p>
              ) : alertsDialog.plan.length === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-emerald-800">
                  ✓ Nessun avviso da inviare. Tutti i clienti hanno indicatori in linea.
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {alertsDialog.plan.map((entry) => (
                    <div key={entry.viewer_id + entry.client_id} className="border rounded-lg p-3">
                      <div className="font-semibold text-sm">{entry.client_name}</div>
                      <div className="text-xs text-slate-500 mb-2">→ {entry.viewer_email}</div>
                      <div className="space-y-1.5">
                        {entry.alerts.map((a) => (
                          <div key={a.key} className="flex items-start gap-2 text-sm">
                            <Badge variant={a.severity === "critical" ? "destructive" : "secondary"} className="mt-0.5 shrink-0">
                              {a.severity === "critical" ? "Critico" : "Attenzione"}
                            </Badge>
                            <div className="flex-1">
                              <div className="font-medium">{a.title}</div>
                              <div className="text-xs text-slate-500">{a.body}</div>
                              {a.already_sent && (
                                <div className="text-[10px] text-slate-400 mt-0.5">Già inviato in precedenza</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {alertsDialog.result && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                  <strong>{alertsDialog.result.pushed}</strong> inviati ·{" "}
                  <strong>{alertsDialog.result.skipped}</strong> saltati ·{" "}
                  <strong>{alertsDialog.result.failed}</strong> falliti
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAlertsDialog(null)}>Chiudi</Button>
            {alertsDialog?.plan && alertsDialog.plan.length > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => runAlerts(true)}
                  disabled={alertsDialog.running}
                >
                  Forza re-invio
                </Button>
                <Button
                  onClick={() => runAlerts(false)}
                  disabled={alertsDialog.running}
                  data-testid="confirm-run-alerts"
                >
                  {alertsDialog.running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BellRing className="h-4 w-4 mr-2" />}
                  Invia notifiche
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
